import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { FormattedMessage, useIntl } from '../../util/reactIntl';

import css from './AISearchModalContent.module.css';

/* ─── Typewriter placeholder phrases ────────────────────────────────────── */
const PLACEHOLDER_IDS = [
  'WedlyHero.searchPlaceholder',
  'WedlyHero.searchPlaceholderRot2',
  'WedlyHero.searchPlaceholderRot3',
  'WedlyHero.searchPlaceholderRot4',
];

/* ─── Keyword suggestion chips ───────────────────────────────────────────── */
const SUGGESTION_IDS = [
  'WedlyHero.hint1',
  'WedlyHero.hint2',
  'WedlyHero.hint3',
  'AISearchModal.suggestion1',
  'AISearchModal.suggestion2',
  'AISearchModal.suggestion3',
  'AISearchModal.suggestion4',
  'AISearchModal.suggestion5',
];

const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M12.5 12.5L16 16" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

const SparkleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 15 15" fill="none" aria-hidden="true">
    <path
      d="M7.5 1L8.6 5.4L13 6.5L8.6 7.6L7.5 12L6.4 7.6L2 6.5L6.4 5.4L7.5 1Z"
      fill="currentColor"
    />
    <path
      d="M12 1L12.5 3L14.5 3.5L12.5 4L12 6L11.5 4L9.5 3.5L11.5 3L12 1Z"
      fill="currentColor"
      opacity="0.6"
    />
  </svg>
);

const ArrowIcon = () => (
  <svg width="14" height="10" viewBox="0 0 14 10" fill="none" aria-hidden="true">
    <path
      d="M1 5h12M8 1l5 4-5 4"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * AI search panel rendered inside the modal — dark-themed, focused search
 * experience with typewriter placeholder, keyword chips, and a prominent CTA.
 *
 * @param {Object}   props
 * @param {Function} props.onSearch — called with the trimmed query string
 */
const AISearchModalContent = ({ onSearch }) => {
  const intl = useIntl();
  const [query, setQuery] = useState('');
  const [animatedPlaceholder, setAnimatedPlaceholder] = useState('');

  const placeholderPhrases = useMemo(
    () => PLACEHOLDER_IDS.map(id => intl.formatMessage({ id })),
    [intl]
  );

  /* ── Typewriter effect ── */
  useEffect(() => {
    if (query.trim()) {
      setAnimatedPlaceholder('');
      return undefined;
    }

    let phraseIdx = 0;
    let charIdx = 0;
    let mode = 'forward';
    let pauseUntil = 0;
    const tickMs = 46;
    const holdMs = 2400;

    const step = () => {
      const now = Date.now();
      const target = placeholderPhrases[phraseIdx];

      if (mode === 'pause') {
        if (now >= pauseUntil) mode = 'backward';
        return;
      }
      if (mode === 'forward') {
        if (charIdx < target.length) {
          charIdx += 1;
          setAnimatedPlaceholder(target.slice(0, charIdx));
        } else {
          mode = 'pause';
          pauseUntil = now + holdMs;
        }
        return;
      }
      if (charIdx > 0) {
        charIdx -= 1;
        setAnimatedPlaceholder(target.slice(0, charIdx));
      } else {
        phraseIdx = (phraseIdx + 1) % placeholderPhrases.length;
        mode = 'forward';
        setAnimatedPlaceholder('');
      }
    };

    const id = setInterval(step, tickMs);
    step();
    return () => clearInterval(id);
  }, [query, placeholderPhrases]);

  const handleSearch = useCallback(() => {
    const q = query.trim();
    if (q) onSearch?.(q);
  }, [onSearch, query]);

  const handleChipClick = useCallback(
    chip => {
      setQuery(chip);
      onSearch?.(chip);
    },
    [onSearch]
  );

  const staticPlaceholder = intl.formatMessage({ id: 'WedlyHero.searchPlaceholder' });

  return (
    <div className={css.root}>
      {/* ── Ambient glow ── */}
      <div className={css.glow} aria-hidden="true" />

      {/* ── Search input ── */}
      <div className={css.inputWrap}>
        <span className={css.inputIcon}>
          <SearchIcon />
        </span>
        <input
          className={css.input}
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSearch()}
          placeholder={query.trim() ? staticPlaceholder : animatedPlaceholder}
          autoComplete="off"
          autoFocus
          aria-label={intl.formatMessage({ id: 'AISearchModal.searchLabel' })}
        />
        {query.trim() ? (
          <button
            type="button"
            className={css.clearBtn}
            onClick={() => setQuery('')}
            aria-label={intl.formatMessage({ id: 'AISearchModal.clearLabel' })}
          >
            ×
          </button>
        ) : null}
      </div>

      {/* ── Keyword suggestions ── */}
      <div className={css.suggestions}>
        <span className={css.suggestionsLabel}>
          <SparkleIcon />
          <FormattedMessage id="AISearchModal.tryLabel" />
        </span>
        <div className={css.chips}>
          {SUGGESTION_IDS.map(id => {
            const text = intl.formatMessage({ id });
            return (
              <button
                key={id}
                type="button"
                className={css.chip}
                onClick={() => handleChipClick(text)}
              >
                {text}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Divider ── */}
      <div className={css.divider} aria-hidden="true" />

      {/* ── Search CTA ── */}
      <button
        type="button"
        className={css.searchBtn}
        onClick={handleSearch}
        disabled={!query.trim()}
      >
        <SparkleIcon />
        <span>
          <FormattedMessage id="AISearchModal.searchButton" />
        </span>
        <ArrowIcon />
      </button>
    </div>
  );
};

AISearchModalContent.propTypes = {
  onSearch: PropTypes.func,
};

export default AISearchModalContent;
